import ArchiveIcon from '@mui/icons-material/Archive';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import ImageIcon from '@mui/icons-material/Image';
import PublishIcon from '@mui/icons-material/Publish';
import VisibilityIcon from '@mui/icons-material/Visibility';
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import PageArea from '../../components/shared/PageArea';
import PageTitle from '../../components/shared/PageTitle';
import { Section } from '../../components/ui/Section';
import RichTextEditor from '../../components/forms/RichTextEditor';
import { UserContext } from '../../components/shared/useUser';
import type {
  NewsAudiencePreview,
  NewsManagementItem,
  NewsTag,
} from '../../app/services/api/news/newsManagementTypes';
import type { NewsComment, NewsItemAnalytics } from '../../app/services/api/news/newsConsumerTypes';
import { useNewsManagementClient } from '../../hooks/api/useNewsManagementClient';
import useNswagClient from '../../hooks/api/useNswagClient';
import { NewsTaxonomyManager } from './NewsTaxonomyManager';

interface FormState {
  title: string;
  summary: string;
  contentHtml: string;
  isPinned: boolean;
  commentsEnabled: boolean;
  reactionsEnabled: boolean;
  targetAllWithinScope: boolean;
  franchiseIds: number[];
  storeNumbers: string[];
  tagIds: number[];
  visibleFromUtc: string;
  visibleUntilUtc: string;
}

const emptyForm: FormState = {
  title: '',
  summary: '',
  contentHtml: '',
  isPinned: false,
  commentsEnabled: false,
  reactionsEnabled: false,
  targetAllWithinScope: true,
  franchiseIds: [],
  storeNumbers: [],
  tagIds: [],
  visibleFromUtc: '',
  visibleUntilUtc: '',
};

const utcToZonedInput = (value: string | null | undefined, timeZone: string | null) => {
  if (!value || !timeZone) return '';
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(new Date(value));
  const get = (type: Intl.DateTimeFormatPartTypes) => parts.find(part => part.type === type)?.value ?? '';
  return `${get('year')}-${get('month')}-${get('day')}T${get('hour')}:${get('minute')}`;
};

const zonedInputToUtc = (value: string, timeZone: string) => {
  const [datePart, timePart] = value.split('T');
  const [year, month, day] = datePart.split('-').map(Number);
  const [hour, minute] = timePart.split(':').map(Number);
  const targetAsUtc = Date.UTC(year, month - 1, day, hour, minute, 0);

  const offsetAt = (instant: number) => {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hourCycle: 'h23',
    }).formatToParts(new Date(instant));
    const get = (type: Intl.DateTimeFormatPartTypes) => Number(parts.find(part => part.type === type)?.value ?? 0);
    const representedAsUtc = Date.UTC(
      get('year'),
      get('month') - 1,
      get('day'),
      get('hour'),
      get('minute'),
      get('second'),
    );
    return representedAsUtc - instant;
  };

  let instant = targetAsUtc - offsetAt(targetAsUtc);
  instant = targetAsUtc - offsetAt(instant);
  return new Date(instant).toISOString();
};

const NewsItemDetails = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const client = useNewsManagementClient();
  const { getStoreTimezone } = useNswagClient();
  const {
    hasPermissionTo,
    selectedFranchise,
    selectedStore,
    user,
    userFranchisesCurrentTenant,
    userStoresCurrentTenant,
  } = useContext(UserContext);

  const isCreate = !id || id === 'new';
  const itemId = isCreate ? null : Number(id);
  const tenantId = selectedFranchise?.tenantId;
  const hasTenantLevelAccess = user?.hasTenantLevelAccess === true;
  const hasFranchiseLevelAccess = user?.hasFranchiseLevelAccess === true;

  const canWrite = hasPermissionTo(['NewsDraftWrite']);
  const canPublish = hasPermissionTo(['NewsPublish']);
  const canArchive = hasPermissionTo(['NewsArchive']);
  const canFeature = hasPermissionTo(['NewsFeature']);
  const canAudience = hasPermissionTo(['NewsAudienceManage']);
  const canTaxonomy = hasPermissionTo(['NewsTaxonomyManage']);
  const canAnalytics = hasPermissionTo(['NewsAnalyticsView']);
  const canModerate = hasPermissionTo(['NewsModerate']);

  const [item, setItem] = useState<NewsManagementItem | null>(null);
  const ownerFranchiseId = item?.franchiseId
    ?? (isCreate && !hasTenantLevelAccess && hasFranchiseLevelAccess
      ? selectedFranchise?.franchiseId ?? null
      : null);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [tags, setTags] = useState<NewsTag[]>([]);
  const [saving, setSaving] = useState(false);
  const [previewHtml, setPreviewHtml] = useState<string | null>(null);
  const [audiencePreview, setAudiencePreview] = useState<NewsAudiencePreview | null>(null);
  const [schedulingTimeZone, setSchedulingTimeZone] = useState<string | null>(null);
  const [heroAltText, setHeroAltText] = useState('');
  const [analytics, setAnalytics] = useState<NewsItemAnalytics | null>(null);
  const [moderationComments, setModerationComments] = useState<NewsComment[]>([]);
  const [moderationTarget, setModerationTarget] = useState<NewsComment | null>(null);
  const [moderationReason, setModerationReason] = useState('');
  const [tagDialogOpen, setTagDialogOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadItem = useCallback(async () => {
    if (!itemId) return;
    const value = await client.getItem(itemId);
    setItem(value);
    setForm({
      title: value.title,
      summary: value.summary,
      contentHtml: value.contentHtml,
      isPinned: value.isPinned,
      commentsEnabled: value.commentsEnabled,
      reactionsEnabled: value.reactionsEnabled,
      targetAllWithinScope: value.audience.targetAllWithinScope,
      franchiseIds: value.audience.franchiseIds,
      storeNumbers: value.audience.stores.map(store => store.storeNumber),
      tagIds: value.tags.map(tag => tag.id),
      visibleFromUtc: value.status === 'Expired' || value.status === 'Archived' ? '' : utcToZonedInput(value.visibleFromUtc, schedulingTimeZone),
      visibleUntilUtc: value.status === 'Expired' || value.status === 'Archived' ? '' : utcToZonedInput(value.visibleUntilUtc, schedulingTimeZone),
    });
    setHeroAltText(value.media.find(media => media.purpose === 'Hero')?.altText ?? '');
  }, [client, itemId, schedulingTimeZone]);

  useEffect(() => {
    if (!selectedStore?.storeNumber || !selectedStore.franchiseName) {
      setSchedulingTimeZone(null);
      return;
    }
    getStoreTimezone(selectedStore.storeNumber, selectedStore.franchiseName)
      .then(response => setSchedulingTimeZone(response.timeZoneId ?? null))
      .catch(() => setSchedulingTimeZone(null));
  }, [getStoreTimezone, selectedStore?.franchiseName, selectedStore?.storeNumber]);

  useEffect(() => {
    if (!tenantId) return;
    client.getTags(tenantId, true)
      .then(setTags)
      .catch(() => setTags([]));
  }, [client, tenantId]);

  useEffect(() => {
    loadItem().catch(() => setError('Unable to load this News item.'));
  }, [loadItem]);

  useEffect(() => {
    if (!item || !canAnalytics) {
      setAnalytics(null);
      return;
    }
    client.getAnalytics(item.id).then(setAnalytics).catch(() => setAnalytics(null));
  }, [canAnalytics, client, item]);

  useEffect(() => {
    if (!item || !canModerate) {
      setModerationComments([]);
      return;
    }
    client.getManagementComments(item.id)
      .then(result => setModerationComments(result.comments))
      .catch(() => setModerationComments([]));
  }, [canModerate, client, item]);

  const availableFranchises = useMemo(
    () => ownerFranchiseId
      ? userFranchisesCurrentTenant.filter(franchise => franchise.franchiseId === ownerFranchiseId)
      : userFranchisesCurrentTenant,
    [ownerFranchiseId, userFranchisesCurrentTenant],
  );

  const availableStores = useMemo(
    () => ownerFranchiseId
      ? userStoresCurrentTenant.filter(store => store.franchiseId === ownerFranchiseId)
      : userStoresCurrentTenant,
    [ownerFranchiseId, userStoresCurrentTenant],
  );

  const selectedFranchises = useMemo(
    () => availableFranchises.filter(franchise => franchise.franchiseId && form.franchiseIds.includes(franchise.franchiseId)),
    [availableFranchises, form.franchiseIds],
  );

  const selectedStores = useMemo(
    () => availableStores.filter(store => form.storeNumbers.includes(store.storeNumber ?? '')),
    [availableStores, form.storeNumbers],
  );

  const setField = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm(current => ({ ...current, [key]: value }));
  };

  const buildAudience = () => ({
    targetAllWithinScope: form.targetAllWithinScope,
    franchiseIds: form.targetAllWithinScope ? [] : form.franchiseIds,
    stores: form.targetAllWithinScope
      ? []
      : selectedStores
        .filter(store => store.franchiseId && store.storeNumber)
        .map(store => ({
          franchiseId: store.franchiseId as number,
          storeNumber: store.storeNumber as string,
        })),
  });

  const validate = () => {
    if (!form.title.trim()) return 'Title is required.';
    if (!form.summary.trim()) return 'Summary is required.';
    if (!form.contentHtml.trim()) return 'Content is required.';
    if (!form.targetAllWithinScope && form.franchiseIds.length === 0 && form.storeNumbers.length === 0) {
      return 'Select at least one franchise or store.';
    }
    return null;
  };

  const saveDraft = async (): Promise<NewsManagementItem | null> => {
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return null;
    }
    if (!tenantId) {
      setError('A tenant context is required.');
      return null;
    }
    if (isCreate && !hasTenantLevelAccess && !ownerFranchiseId) {
      setError('News authoring requires tenant or franchise-level access.');
      return null;
    }

    setSaving(true);
    setError(null);
    try {
      const saved = item
        ? await client.update(item.id, {
          title: form.title.trim(),
          summary: form.summary.trim(),
          contentHtml: form.contentHtml,
          tagIds: form.tagIds,
          isPinned: form.isPinned,
          commentsEnabled: form.commentsEnabled,
          reactionsEnabled: form.reactionsEnabled,
          audience: buildAudience(),
          rowVersion: item.rowVersion,
        })
        : await client.create({
          tenantId,
          franchiseId: ownerFranchiseId,
          title: form.title.trim(),
          summary: form.summary.trim(),
          contentHtml: form.contentHtml,
          tagIds: form.tagIds,
          isPinned: form.isPinned,
          commentsEnabled: form.commentsEnabled,
          reactionsEnabled: form.reactionsEnabled,
          audience: buildAudience(),
        });

      setItem(saved);
      if (isCreate) {
        navigate(`/news-management/${saved.id}`, { replace: true });
      }
      return saved;
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Unable to save this News item.');
      return null;
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = async () => {
    if (!canPublish) return;
    const publishTarget = canWrite ? await saveDraft() : item;
    if (!publishTarget) return;

    setSaving(true);
    try {
      const published = await client.publish(
        publishTarget.id,
        publishTarget.rowVersion,
        form.visibleFromUtc
          ? schedulingTimeZone
            ? zonedInputToUtc(form.visibleFromUtc, schedulingTimeZone)
            : (() => { throw new Error('Scheduling timezone is unavailable.'); })()
          : null,
        form.visibleUntilUtc
          ? schedulingTimeZone
            ? zonedInputToUtc(form.visibleUntilUtc, schedulingTimeZone)
            : (() => { throw new Error('Scheduling timezone is unavailable.'); })()
          : null,
      );
      setItem(published);
    } finally {
      setSaving(false);
    }
  };

  const handleArchive = async () => {
    if (!item || !canArchive) return;
    setSaving(true);
    try {
      setItem(await client.archive(item.id, item.rowVersion));
    } finally {
      setSaving(false);
    }
  };

  const handlePreview = async () => {
    const preview = await client.preview(form.contentHtml);
    setPreviewHtml(preview.contentHtml);
  };

  const handleAudiencePreview = async () => {
    if (!item) return;
    const preview = await client.getAudiencePreview(item.id);
    setAudiencePreview(preview);
  };

  const handleHeroUpload = async (file?: File) => {
    if (!item || !file) return;
    if (!heroAltText.trim()) {
      setError('Hero image alternative text is required.');
      return;
    }
    const updated = await client.uploadHero(item.id, file, heroAltText.trim(), item.rowVersion);
    setItem(updated);
  };

  const handleAttachmentUpload = async (file?: File) => {
    if (!item || !file) return;
    const updated = await client.uploadAttachment(item.id, file, item.rowVersion, item.media.filter(media => media.purpose === 'Attachment').length);
    setItem(updated);
  };

  const flattenedModerationComments = useMemo(() => {
    const flatten = (comments: NewsComment[]): NewsComment[] =>
      comments.flatMap(comment => [comment, ...flatten(comment.replies ?? [])]);
    return flatten(moderationComments);
  }, [moderationComments]);

  const handleModerateComment = async () => {
    if (!item || !moderationTarget || !moderationReason.trim()) return;
    await client.moderateComment(item.id, moderationTarget.id, moderationReason.trim(), moderationTarget.rowVersion);
    const refreshed = await client.getManagementComments(item.id);
    setModerationComments(refreshed.comments);
    setModerationTarget(null);
    setModerationReason('');
  };

  const analyticsTotals = analytics?.publications.reduce((totals, publication) => ({
    views: totals.views + publication.totalViews,
    readers: totals.readers + publication.uniqueReaders,
    reactions: totals.reactions + publication.reactionCount,
    comments: totals.comments + publication.commentCount,
  }), { views: 0, readers: 0, reactions: 0, comments: 0 });

  const hero = item?.media.find(media => media.purpose === 'Hero');
  const attachments = item?.media.filter(media => media.purpose === 'Attachment') ?? [];

  return (
    <PageArea>
      <PageTitle subTitle="Author, target and publish operational communications">
        {item ? item.title : 'Create News Item'}
      </PageTitle>

      {error && (
        <Box sx={{ mb: 3 }}>
          <Chip label={error}
            color="error"
            variant="outlined" />
        </Box>
      )}

      <Grid container
        spacing={4}>
        <Grid item
          xs={12}
          lg={8}>
          <Section>
            <Stack spacing={3}>
              <TextField
                label="Title"
                value={form.title}
                onChange={event => setField('title', event.target.value)}
                disabled={!canWrite}
                fullWidth
                inputProps={{ maxLength: 200 }}
              />
              <TextField
                label="Feed summary"
                value={form.summary}
                onChange={event => setField('summary', event.target.value)}
                disabled={!canWrite}
                fullWidth
                multiline
                minRows={2}
                inputProps={{ maxLength: 1000 }}
              />
              <Box>
                <Typography variant="textSM"
                  sx={{ mb: 1 }}>Article content</Typography>
                <RichTextEditor
                  value={form.contentHtml}
                  onChange={value => setField('contentHtml', value)}
                  readOnly={!canWrite}
                  enableDocumentLinking={false}
                  allowImages={false}
                />
              </Box>
              <Stack direction="row"
                spacing={2}
                flexWrap="wrap">
                {canWrite && (
                  <Button
                    variant="secondary"
                    size="md"
                    startIcon={<VisibilityIcon />}
                    onClick={() => handlePreview().catch(() => setError('Unable to preview this content.'))}
                  >
                    Preview
                  </Button>
                )}
                {canWrite && (
                  <Button
                    variant="secondary"
                    size="md"
                    disabled={saving}
                    onClick={() => saveDraft().catch(() => undefined)}
                  >
                    Save Draft
                  </Button>
                )}
                {canPublish && (
                  <Button
                    variant="primary"
                    size="md"
                    startIcon={<PublishIcon />}
                    disabled={saving}
                    onClick={() => handlePublish().catch(() => setError('Unable to publish this News item.'))}
                  >
                    {form.visibleFromUtc && new Date(form.visibleFromUtc) > new Date() ? 'Schedule' : 'Publish'}
                  </Button>
                )}
                {item && canArchive && ['Published', 'Scheduled'].includes(item.status) && (
                  <Button
                    variant="secondary"
                    size="md"
                    startIcon={<ArchiveIcon />}
                    disabled={saving}
                    onClick={() => handleArchive().catch(() => setError('Unable to archive this News item.'))}
                  >
                    Archive
                  </Button>
                )}
              </Stack>
            </Stack>
          </Section>

          <Section style={{ mt: 3 }}>
            <Typography variant="textMD"
              fontWeight={600}
              sx={{ mb: 2 }}>Media</Typography>
            {!item ? (
              <Typography variant="textSM"
                color="text.secondary">
                Save the draft before adding a hero image or attachments.
              </Typography>
            ) : (
              <Stack spacing={3}>
                <TextField
                  label="Hero image alternative text"
                  value={heroAltText}
                  onChange={event => setHeroAltText(event.target.value)}
                  disabled={!canWrite}
                  fullWidth
                />
                {hero?.url && (
                  <Box
                    component="img"
                    src={hero.url}
                    alt={hero.altText || form.title}
                    sx={{ width: '100%', maxWidth: 520, maxHeight: 280, objectFit: 'cover', borderRadius: 2 }}
                  />
                )}
                <Stack direction="row"
                  spacing={2}
                  flexWrap="wrap">
                  {canWrite && (
                    <Button
                      component="label"
                      variant="secondary"
                      size="md"
                      startIcon={<ImageIcon />}
                    >
                      {hero ? 'Replace Hero' : 'Upload Hero'}
                      <input
                        hidden
                        type="file"
                        accept="image/jpeg,image/png,image/webp"
                        aria-label="Hero image"
                        onChange={event => handleHeroUpload(event.target.files?.[0]).catch(() => setError('Unable to upload hero image.'))}
                      />
                    </Button>
                  )}
                  {hero && canWrite && (
                    <Button
                      variant="secondary"
                      size="md"
                      onClick={() => client.removeHero(item.id, item.rowVersion).then(setItem).catch(() => setError('Unable to remove hero image.'))}
                    >
                      Remove Hero
                    </Button>
                  )}
                  {canWrite && (
                    <Button
                      component="label"
                      variant="secondary"
                      size="md"
                      startIcon={<AttachFileIcon />}
                    >
                      Add Attachment
                      <input
                        hidden
                        type="file"
                        aria-label="Attachment"
                        onChange={event => handleAttachmentUpload(event.target.files?.[0]).catch(() => setError('Unable to upload attachment.'))}
                      />
                    </Button>
                  )}
                </Stack>
                {attachments.map(media => (
                  <Stack
                    key={media.id}
                    direction="row"
                    spacing={2}
                    alignItems="center"
                    justifyContent="space-between"
                  >
                    <Typography variant="textSM">{media.originalFileName}</Typography>
                    {canWrite && (
                      <Button
                        variant="secondary"
                        size="md"
                        onClick={() => client.removeAttachment(item.id, media.id, item.rowVersion).then(setItem).catch(() => setError('Unable to remove attachment.'))}
                      >
                        Remove
                      </Button>
                    )}
                  </Stack>
                ))}
              </Stack>
            )}
          </Section>
        </Grid>

        <Grid item
          xs={12}
          lg={4}>
          <Section>
            <Stack spacing={3}>
              <Typography variant="textMD"
                fontWeight={600}>Publishing</Typography>
              {item && <Chip label={item.status}
                variant="outlined" />}
              <FormControlLabel
                control={(
                  <Checkbox
                    checked={form.isPinned}
                    disabled={!canFeature || !canWrite}
                    onChange={event => setField('isPinned', event.target.checked)}
                  />
                )}
                label="Feature this communication"
              />
              <FormControlLabel
                control={(
                  <Checkbox
                    checked={form.commentsEnabled}
                    disabled={!canWrite}
                    onChange={event => setField('commentsEnabled', event.target.checked)}
                  />
                )}
                label="Allow comments"
              />
              <FormControlLabel
                control={(
                  <Checkbox
                    checked={form.reactionsEnabled}
                    disabled={!canWrite}
                    onChange={event => setField('reactionsEnabled', event.target.checked)}
                  />
                )}
                label="Allow likes"
              />
              <TextField
                label="Visible from"
                type="datetime-local"
                value={form.visibleFromUtc}
                onChange={event => setField('visibleFromUtc', event.target.value)}
                InputLabelProps={{ shrink: true }}
                disabled={!canPublish || (!!form.visibleFromUtc && !schedulingTimeZone)}
                helperText={schedulingTimeZone ? `Scheduling timezone: ${schedulingTimeZone}` : 'Select a store context to resolve the business timezone.'}
              />
              <TextField
                label="Visible until"
                type="datetime-local"
                value={form.visibleUntilUtc}
                onChange={event => setField('visibleUntilUtc', event.target.value)}
                InputLabelProps={{ shrink: true }}
                disabled={!canPublish || (!!form.visibleUntilUtc && !schedulingTimeZone)}
                helperText={schedulingTimeZone ? `Scheduling timezone: ${schedulingTimeZone}` : 'Select a store context to resolve the business timezone.'}
              />

              <Divider />
              <Typography variant="textMD"
                fontWeight={600}>Audience</Typography>
              <FormControlLabel
                control={(
                  <Checkbox
                    checked={form.targetAllWithinScope}
                    disabled={!canAudience || !canWrite}
                    onChange={event => setField('targetAllWithinScope', event.target.checked)}
                  />
                )}
                label={ownerFranchiseId
                  ? `Entire ${selectedFranchise?.franchiseName ?? 'franchise'} (all child stores)`
                  : 'Entire tenant (all child franchises and stores)'}
              />
              {!form.targetAllWithinScope && (
                <>
                  <Autocomplete
                    multiple
                    options={availableFranchises}
                    value={selectedFranchises}
                    getOptionLabel={franchise => franchise.franchiseName ?? `Franchise ${franchise.franchiseId}`}
                    isOptionEqualToValue={(option, value) => option.franchiseId === value.franchiseId}
                    onChange={(_, value) => setField('franchiseIds', value.map(franchise => franchise.franchiseId).filter((id): id is number => !!id))}
                    disabled={!canAudience || !canWrite}
                    renderInput={params => <TextField {...params}
                      label="Whole franchises"
                      helperText="Selecting a franchise includes all of its child stores." />}
                  />
                  <Autocomplete
                    multiple
                    options={availableStores}
                    value={selectedStores}
                    getOptionLabel={store => `${store.storeNumber ?? ''} - ${store.storeName ?? ''}`}
                    isOptionEqualToValue={(option, value) => option.storeNumber === value.storeNumber}
                    onChange={(_, value) => setField('storeNumbers', value.map(store => store.storeNumber ?? '').filter(Boolean))}
                    disabled={!canAudience || !canWrite}
                    renderInput={params => <TextField {...params}
                      label="Individual stores"
                      helperText="Use for leaf-level exceptions or store-specific communications." />}
                  />
                </>
              )}
              {item && (
                <Button
                  variant="secondary"
                  size="md"
                  onClick={() => handleAudiencePreview().catch(() => setError('Unable to resolve the audience.'))}
                >
                  {audiencePreview === null ? 'Preview Audience' : `${audiencePreview.totalLocations} locations targeted`}
                </Button>
              )}
              {audiencePreview && (
                <Stack spacing={0.5}>
                  {audiencePreview.locations.slice(0, 20).map(location => (
                    <Typography key={`${location.franchiseId}-${location.storeNumber}`}
                      variant="textXS">
                      Franchise {location.franchiseId} - Store {location.storeNumber}
                    </Typography>
                  ))}
                  {audiencePreview.totalLocations > audiencePreview.locations.slice(0, 20).length && (
                    <Typography variant="textXS"
                      color="text.secondary">
                      + {audiencePreview.totalLocations - audiencePreview.locations.slice(0, 20).length} more locations
                    </Typography>
                  )}
                </Stack>
              )}

              <Divider />
              <Stack direction="row"
                alignItems="center"
                justifyContent="space-between">
                <Typography variant="textMD"
                  fontWeight={600}>Tags</Typography>
                {canTaxonomy && (
                  <Button
                    variant="secondary"
                    size="md"
                    onClick={() => setTagDialogOpen(true)}
                  >
                    Manage Tags
                  </Button>
                )}
              </Stack>
              <Autocomplete
                multiple
                options={tags}
                value={tags.filter(tag => form.tagIds.includes(tag.id))}
                getOptionLabel={tag => tag.name}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                onChange={(_, value) => setField('tagIds', value.map(tag => tag.id))}
                disabled={!canWrite}
                renderInput={params => <TextField {...params}
                  label="Tags" />}
              />

              {canAnalytics && analyticsTotals && (
                <>
                  <Divider />
                  <Typography variant="textMD"
                    fontWeight={600}>Communication Performance</Typography>
                  <Stack spacing={1}>
                    <Typography variant="textSM">Views: {analyticsTotals.views}</Typography>
                    <Typography variant="textSM">Unique readers: {analyticsTotals.readers}</Typography>
                    <Typography variant="textSM">Likes: {analyticsTotals.reactions}</Typography>
                    <Typography variant="textSM">Comments: {analyticsTotals.comments}</Typography>
                    <Typography variant="textXS"
                      color="text.secondary">
                      As of {new Date(analytics?.asOfUtc ?? '').toLocaleString('en-GB')}
                    </Typography>
                  </Stack>
                </>
              )}

              {canModerate && item && (
                <>
                  <Divider />
                  <Typography variant="textMD"
                    fontWeight={600}>Comment Moderation</Typography>
                  {flattenedModerationComments.length === 0 ? (
                    <Typography variant="textSM"
                      color="text.secondary">No comments to moderate.</Typography>
                  ) : (
                    <Stack spacing={1.5}>
                      {flattenedModerationComments.map(comment => (
                        <Box key={comment.id}
                          sx={{ p: 1.5, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                          <Typography variant="textSM"
                            fontWeight={600}>
                            {comment.userDisplayName || `User ${comment.userId}`}
                          </Typography>
                          <Typography variant="textXS"
                            color="text.secondary">
                            {new Date(comment.createdAtUtc).toLocaleString('en-GB')}
                          </Typography>
                          <Typography variant="textSM"
                            sx={{ my: 1 }}>
                            {comment.isDeleted ? 'Comment removed' : comment.content}
                          </Typography>
                          {!comment.isDeleted && (
                            <Button
                              variant="secondary"
                              size="md"
                              onClick={() => {
                                setModerationTarget(comment);
                                setModerationReason('');
                              }}
                            >
                              Remove Comment
                            </Button>
                          )}
                        </Box>
                      ))}
                    </Stack>
                  )}
                </>
              )}
            </Stack>
          </Section>
        </Grid>
      </Grid>

      <Dialog
        open={moderationTarget !== null}
        onClose={() => {
          setModerationTarget(null);
          setModerationReason('');
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Remove Comment</DialogTitle>
        <DialogContent>
          <TextField
            label="Moderation reason"
            value={moderationReason}
            onChange={event => setModerationReason(event.target.value)}
            multiline
            minRows={3}
            inputProps={{ maxLength: 500 }}
            fullWidth
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            variant="secondary"
            size="md"
            onClick={() => {
              setModerationTarget(null);
              setModerationReason('');
            }}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            size="md"
            disabled={!moderationReason.trim()}
            onClick={() => handleModerateComment().catch(() => setError('Unable to moderate this comment.'))}
          >
            Remove Comment
          </Button>
        </DialogActions>
      </Dialog>

      {tenantId && canTaxonomy && (
        <NewsTaxonomyManager
          open={tagDialogOpen}
          tenantId={tenantId}
          onClose={() => setTagDialogOpen(false)}
          onChanged={() => {
            client.getTags(tenantId, true)
              .then(setTags)
              .catch(() => setTags([]));
          }}
        />
      )}

      <Dialog
        open={previewHtml !== null}
        onClose={() => setPreviewHtml(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>News Preview</DialogTitle>
        <DialogContent>
          <Typography variant="textXL"
            fontWeight={700}
            sx={{ mb: 1 }}>{form.title}</Typography>
          <Typography variant="textSM"
            color="text.secondary"
            sx={{ mb: 3 }}>{form.summary}</Typography>
          <Box dangerouslySetInnerHTML={{ __html: previewHtml ?? '' }} />
        </DialogContent>
        <DialogActions>
          <Button variant="secondary"
            size="md"
            onClick={() => setPreviewHtml(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </PageArea>
  );
};

export default NewsItemDetails;
